import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";
import {
  MessageCircle,
  X,
  RotateCcw,
  Loader2,
  Send,
  Square,
  Sparkles,
  ThumbsUp,
  ThumbsDown,
  Edit3,
  Plus,
  BookOpen,
  Terminal,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Copy,
  RefreshCw,
  Filter,
  ListFilter,
  Sliders,
  PanelLeftOpen,
  MoreHorizontal,
  HelpCircle,
} from "lucide-react";
import {
  buildCacheKey,
  getCached,
  setCached,
  type CacheParams,
  type QuestionsMeta,
} from "../utils/suggestedQuestionsCache";
import { getRecentQuestions, addRecentQuestion } from "../utils/recentQuestionsStorage";
import {
  UserContextSurvey,
  markProfileInviteShownThisSession,
  wasProfileInviteShownThisSession,
  setUserContextModalDismissed,
} from "./UserContextSurvey";
import { useMobile } from "../hooks/useMobile";
import { useUser } from "../contexts/UserContext";
import type { UserContextAnswers } from "../types";

const FEEDBACK_DISLIKE_REASONS: { value: string; label: string }[] = [
  { value: "inaccurate", label: "Неточный ответ" },
  { value: "off_topic", label: "Не по теме" },
  { value: "bad_code_example", label: "Плохой пример кода" },
  { value: "other", label: "Другое" },
];

const GROUPS: { key: ChatGroupKey; label: string; Icon: typeof BookOpen }[] = [
  { key: "courses", label: "Курсы", Icon: BookOpen },
  { key: "simulator", label: "Тренажёр", Icon: Terminal },
  { key: "tutor", label: "Tutor", Icon: MessageCircle },
];

/** Промпты в UI и include_debug — только при локальной разработке (`vite dev`), не в production build. */
const CAN_DEBUG_PROMPTS = import.meta.env.DEV;

/** Убирает из текста служебную строку [ПОДБОРКА: id1, id2, ...], чтобы не показывать её пользователю. */
function stripTasklistPayloadLine(text: string): string {
  return text.replace(/\n?\[ПОДБОРКА:\s*[^\]]+\]\s*\n?/g, "").trim();
}

function TooltipPortal({ text, anchorRef }: { text: string; anchorRef: React.RefObject<HTMLElement | null> }) {
  if (!text || !anchorRef.current) return null;
  const rect = anchorRef.current.getBoundingClientRect();
  const top = rect.top + rect.height / 2;
  const left = rect.left - 8;
  return createPortal(
    <div
      className="fixed z-[9999] px-3 py-2 text-xs font-medium text-white bg-neutral-text rounded-lg shadow-xl max-w-[200px] pointer-events-none whitespace-normal"
      style={{ top: `${top}px`, left: `${left}px`, transform: "translate(-100%, -50%)" }}
      role="tooltip"
    >
      {text}
    </div>,
    document.body
  );
}

function ChatSidebar({
  chatList,
  currentChatId,
  selectedGroup,
  activeGroup,
  onSelectGroup,
  onSelectChat,
  onDeleteChat,
  onCreateChat,
  drawerMode = false,
}: {
  chatList: ChatListItem[];
  currentChatId: string;
  selectedGroup: ChatGroupKey | null;
  activeGroup: ChatGroupKey;
  onSelectGroup: (key: ChatGroupKey | null) => void;
  onSelectChat: (id: string) => void;
  onDeleteChat: (id: string) => void;
  /** Создать новый чат для текущего контекста (группа определяется contextType в ChatCore) */
  onCreateChat: () => void;
  /** Мобильный drawer: полная ширина, все группы с подписями */
  drawerMode?: boolean;
}) {
  const [groupHover, setGroupHover] = useState<ChatGroupKey | null>(null);
  const [chatHover, setChatHover] = useState<string | null>(null);
  const grouped = groupChatsByPage(chatList);
  const hasExpandedFolder = drawerMode || selectedGroup !== null;
  const isExpanded = (key: ChatGroupKey) => (drawerMode ? true : selectedGroup === key);

  return (
    <div
      className={`flex flex-col overflow-hidden shrink-0 transition-all duration-200 ${
        drawerMode ? "w-full border-0 bg-neutral-light/30" : `border-l border-neutral-border bg-neutral-light/30 ${hasExpandedFolder ? "w-[200px]" : "w-[56px]"}`
      }`}
      role="navigation"
      aria-label="История чатов"
    >
      <div className="p-1.5 border-b border-neutral-border flex flex-col gap-0.5">
        {GROUPS.map(({ key, label, Icon }) => (
          <FolderGroup
            key={key}
            groupKey={key}
            label={label}
            Icon={Icon}
            chats={grouped[key]}
            expanded={isExpanded(key)}
            isActive={key === activeGroup}
            currentChatId={currentChatId}
            groupHover={groupHover}
            chatHover={chatHover}
            hasExpandedFolder={hasExpandedFolder}
            onToggle={() => onSelectGroup(isExpanded(key) ? null : key)}
            onGroupHover={setGroupHover}
            onSelectChat={onSelectChat}
            onDeleteChat={onDeleteChat}
            onChatHover={setChatHover}
            onCreateChat={onCreateChat}
          />
        ))}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto" />
    </div>
  );
}

function FolderGroup({
  groupKey,
  label,
  Icon,
  chats,
  expanded,
  isActive,
  currentChatId,
  groupHover,
  chatHover,
  hasExpandedFolder,
  onToggle,
  onGroupHover,
  onSelectChat,
  onDeleteChat,
  onChatHover,
  onCreateChat,
}: {
  groupKey: ChatGroupKey;
  label: string;
  Icon: typeof BookOpen;
  chats: ChatListItem[];
  expanded: boolean;
  isActive: boolean;
  currentChatId: string;
  groupHover: ChatGroupKey | null;
  chatHover: string | null;
  hasExpandedFolder: boolean;
  onToggle: () => void;
  onGroupHover: (key: ChatGroupKey | null) => void;
  onSelectChat: (id: string) => void;
  onDeleteChat: (id: string) => void;
  onChatHover: (id: string | null) => void;
  onCreateChat: () => void;
}) {
  const ref = useRef<HTMLButtonElement>(null);

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-1">
        <button
          ref={ref}
          type="button"
          onClick={onToggle}
          onMouseEnter={() => onGroupHover(groupKey)}
          onMouseLeave={() => onGroupHover(null)}
          className={`min-h-[40px] flex-1 flex items-center gap-2 px-2 py-2 rounded-xl hover:bg-neutral-light/80 transition ${
            hasExpandedFolder ? "text-left" : "justify-center"
          } ${isActive ? "bg-primary/10 text-primary" : "text-neutral-text"} ${expanded ? "ring-1 ring-primary/20" : ""}`}
          aria-expanded={expanded}
          aria-label={`${label}, ${chats.length} чатов`}
        >
          <Icon className="w-5 h-5 shrink-0" strokeWidth={2} />
          {hasExpandedFolder && <span className="text-sm font-medium truncate flex-1">{label}</span>}
          {hasExpandedFolder &&
            (expanded ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />)}
        </button>
        {hasExpandedFolder && expanded && isActive && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onCreateChat();
            }}
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-neutral-light transition text-neutral-text/70 hover:text-neutral-text shrink-0"
            title="Новый чат"
            aria-label={`Создать новый чат в группе «${label}»`}
          >
            <Plus className="w-4 h-4" />
          </button>
        )}
      </div>
      {groupHover === groupKey && !hasExpandedFolder && <TooltipPortal text={label} anchorRef={ref} />}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0, overflow: "hidden" }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <div className="pl-3 pr-1 py-1 flex flex-col gap-0.5">
              {chats.length === 0 ? (
                <span className="text-xs text-neutral-text/50 py-2">Нет чатов</span>
              ) : (
                chats.map((c) => (
                  <ChatFolderItem
                    key={c.sessionId}
                    chat={{ id: c.sessionId, title: c.title || "Новый чат" }}
                    Icon={Icon}
                    isActive={c.sessionId === currentChatId}
                    onSelect={() => onSelectChat(c.sessionId)}
                    onDelete={() => onDeleteChat(c.sessionId)}
                    showTooltip={chatHover === c.sessionId}
                    onHoverChange={(h) => onChatHover(h ? c.sessionId : null)}
                  />
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ChatFolderItem({
  chat,
  Icon,
  isActive,
  onSelect,
  onDelete,
  showTooltip,
  onHoverChange,
}: {
  chat: { id: string; title: string };
  Icon: typeof BookOpen;
  isActive: boolean;
  onSelect: () => void;
  onDelete: () => void;
  showTooltip: boolean;
  onHoverChange: (show: boolean) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={ref}
      className="group/item flex items-center min-h-[36px] gap-2 rounded-lg hover:bg-neutral-light/60 transition"
      onMouseEnter={() => onHoverChange(true)}
      onMouseLeave={() => onHoverChange(false)}
    >
      <button
        type="button"
        onClick={onSelect}
        className="flex-1 min-w-0 flex items-center gap-2 text-left py-1.5 rounded focus:outline-none focus:ring-2 focus:ring-primary/30"
        aria-current={isActive ? "true" : undefined}
      >
        <Icon className={`w-4 h-4 shrink-0 ${isActive ? "text-primary" : "text-neutral-text/70"}`} />
        <span className={`text-xs truncate flex-1 ${isActive ? "font-medium text-primary" : "text-neutral-text"}`}>
          {chat.title}
        </span>
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="opacity-0 group-hover/item:opacity-100 p-1 rounded hover:bg-red-100 text-neutral-text/60 hover:text-red-600 transition shrink-0"
        aria-label={`Удалить «${chat.title}»`}
      >
        <X className="w-3.5 h-3.5" />
      </button>
      {showTooltip && <TooltipPortal text={chat.title} anchorRef={ref} />}
    </div>
  );
}

import { API_HOST } from "../constants";
import { AITutorSources } from "./AITutorSources";
import { AITutorMessageContent, AITutorTypingIndicator } from "./AITutorMessageContent";
import { QuizCard } from "./QuizCard";
import { CollapsibleBlock } from "./CollapsibleBlock";
import { extractFollowUpQuestions } from "../utils/formatting/parseAIMessage";
import {
  fetchAllChatList,
  deleteChatSessionOnServer,
  setLastOpenedSessionId,
  getLastOpenedSessionId,
  groupChatsByPage,
  contextToGroup,
  selectSessionForPageContext,
  saveQuizAnswer,
  type ChatGroupKey,
  type ChatListItem,
} from "../utils/chatStorage";
import type { TelegramUser } from "../types";
import type {
  ChatContextType,
  QueryMetrics,
  ChatMessage,
  ModulesMetadataItem,
  QuizData,
} from "../types/chat";

/** Максимум сообщений в одном чате; при достижении создаётся новый чат и показывается предупреждение */
const MAX_CHAT_MESSAGES = 50;

/** Приветственное сообщение по контексту */
const WELCOME_BY_CONTEXT: Record<ChatContextType, string> = {
  generic: "Привет! Спроси что угодно о ClickHouse — помогу разобраться.",
  course: "Привет! Спроси о курсе — помогу с навигацией и выбором темы.",
  lesson: "Привет! Чем могу помочь?",
  editor: "Привет! Нужна помощь с задачей? Задай вопрос — подскажу, не выдавая готового решения.",
  tasklist: "Привет! Могу подсказать, какую задачу выбрать по теме или сложности, или показать подсказки по странице.",
};

/** Fallback-вопросы, если API не ответил (синхронно с backend suggested_questions_service) */
const DEFAULT_QUESTIONS: Record<ChatContextType, string[]> = {
  generic: [
    "Что такое ClickHouse?",
    "Покажи пример SELECT запроса",
    "Как работает MergeTree?",
    "Чем колоночное хранение лучше строкового?",
  ],
  course: [
    "О чём этот курс?",
    "Чему я научусь?",
    "С чего лучше начать?",
    "Какие темы затрагивает первый модуль?",
    "Что я узнаю после прохождения курса?",
  ],
  lesson: [
    "Сделай краткий пересказ урока",
    "Что я узнаю в этом уроке?",
    "Какие концепции ClickHouse здесь важны?",
    "Какую практическую задачу я решу?",
  ],
  editor: [
    "Какие таблицы мне доступны?",
    "Подскажи, с чего начать?",
    "Какой синтаксис использовать для этой задачи?",
    "Почему мой запрос не работает?",
    "Какие функции ClickHouse подойдут?",
  ],
  tasklist: [
    "Какую задачу выбрать по теме?",
    "Покажи лёгкие задачи для начала",
    "Какие задачи по домену e-commerce?",
    "Показать онбординг по странице задач",
  ],
};

/** Загружает историю чата из API и преобразует в ChatMessage[]. */
async function loadChatHistory(apiHost: string, sessionId: string, welcomeMessage: string): Promise<ChatMessage[]> {
  const res = await fetch(`${apiHost}/api/ai/chats/${encodeURIComponent(sessionId)}`);
  if (!res.ok) return [{ role: "assistant", content: welcomeMessage }];
  const data = await res.json();
  const msgs: ChatMessage[] = (data.messages || []).map((m: {
    id: number;
    role: string;
    content: string;
    sources?: { content: string; metadata: Record<string, unknown>; score: number }[] | null;
    suggested_questions?: string[] | null;
    quiz?: { id: number; question: string; options: string[]; correct_index: number; user_answer_index?: number | null } | null;
  }) => {
    const msg: ChatMessage = {
      role: m.role as "user" | "assistant",
      content: m.content,
      messageId: m.id,
    };
    if (m.sources?.length) msg.sources = m.sources;
    if (m.suggested_questions?.length) msg.suggestedQuestions = m.suggested_questions;
    if (m.quiz) {
      msg.quiz = {
        question: m.quiz.question,
        options: m.quiz.options,
        correctIndex: m.quiz.correct_index,
        quizId: m.quiz.id,
        userAnswerIndex: m.quiz.user_answer_index ?? null,
      };
    }
    return msg;
  });
  return msgs.length > 0 ? msgs : [{ role: "assistant", content: welcomeMessage }];
}

/** Самосворачиваемый блок «Что спросить дальше» — аналог QuizCard */
const FollowUpSuggestionsBlock = ({
  questions,
  isLoadingFollowUp,
  onSend,
  getAnswered,
}: {
  questions: string[];
  isLoadingFollowUp: boolean;
  onSend: (q: string) => void;
  getAnswered: () => Set<string>;
}) => {
  const [open, setOpen] = useState(false);
  const answered = getAnswered();
  return (
    <div className="mt-2 rounded-xl border border-primary/20 bg-primary/5 shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-primary/10 transition-colors rounded-xl cursor-pointer"
      >
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/15 text-primary shrink-0">
          <Sparkles className="w-3.5 h-3.5" />
        </span>
        <span className="text-sm font-semibold text-primary/90 flex-1">Что спросить дальше</span>
        {questions.length > 0 && (
          <span className="text-[10px] rounded-full px-1.5 py-0.5 bg-primary/15 text-primary shrink-0">
            {questions.length}
          </span>
        )}
        <ChevronRight className={`w-3.5 h-3.5 text-primary/50 transition-transform duration-150 shrink-0 ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 border-t border-primary/10 pt-2.5">
          <AnimatePresence mode="wait">
            {isLoadingFollowUp ? (
              <motion.div key="skeleton" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-wrap gap-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-11 rounded-lg bg-neutral-light/80 animate-pulse" style={{ width: `${70 + (i % 2) * 30}px` }} />
                ))}
              </motion.div>
            ) : (
              <motion.div key={questions.join("|")} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-wrap gap-2">
                {questions.map((q, i) => {
                  const isAnswered = answered.has(q);
                  return (
                    <motion.button
                      key={q}
                      type="button"
                      onClick={() => onSend(q)}
                      className={`text-xs px-2.5 py-2 min-h-[44px] rounded-2xl border text-left transition-all focus:outline-none focus:ring-2 focus:ring-primary/30 ${
                        isAnswered
                          ? "border-primary/35 bg-primary/12 text-primary"
                          : "border-primary/15 bg-white/95 hover:border-primary/35 hover:bg-white hover:shadow-sm text-neutral-text"
                      }`}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.04, duration: 0.2 }}
                    >
                      {q}
                    </motion.button>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export interface ChatCoreProps {
  contextType: ChatContextType;
  user: TelegramUser | null;
  onClose: () => void;
  embedded?: boolean;
  showCloseButton?: boolean;
  /** Скрыть header (для встраивания в кастомный layout, напр. AITutor standalone) */
  hideHeader?: boolean;

  courseSlug?: string;
  courseTitle?: string;
  modulesMetadata?: ModulesMetadataItem[];

  lessonId?: string;
  lessonTitle?: string;
  lessonContent?: string;

  taskId?: string | number;
  taskTitle?: string;
  userCode?: string;
  queryMetrics?: QueryMetrics;

  containerClassName?: string;
  /** Подсказка в заголовке (рядом с «AI Tutor») */
  headerSubtitle?: string;
  /** Показывать кнопки оценки для fine-tuning (👍/👎) */
  enableFeedback?: boolean;
  /** Показывать sidebar с историей чатов (до 10 диалогов) */
  showChatHistory?: boolean;
  /** Показывать sidebar даже при embedded (для standalone AI Tutor) */
  showChatHistoryInEmbedded?: boolean;
  /** Колбэк при изменении требуемой ширины панели (для embedded, чтобы родитель подстроил aside) */
  onMinWidthChange?: (width: number) => void;
  /** Перед отправкой сообщения: если вернёт true, сообщение не отправляется (обработано родителем) */
  onBeforeSendMessage?: (message: string) => boolean;
  /** Каталог задач тренажёра для contextType "tasklist" (подборки по запросу) */
  tasksMetadata?: Array<{ id: number; title: string; description?: string; domain?: string; difficulty?: string; tag?: string[] }>;
  /** Применить подборку задач в списке (tasklist): фильтр по переданным id */
  onApplyTaskSelection?: (taskIds: number[]) => void;
  /** Источник начальных вопросов: "ai_tutor_page" — запрос с source=ai_tutor_page для generic (статичные вопросы из конфига BE) */
  initialQuestionsSource?: "ai_tutor_page";
  /** Ключ страницы для онбординга (tasklist, simulator, sandbox, lessonpage, datadocs) — показывать вопрос «Как пользоваться [страница]» */
  onboardingPageKey?: string;
  /** Завершённые онбординги (из API или localStorage) — вопрос не показывается, если page_key в списке */
  onboardingCompletedPages?: string[];
  /** Пока true (загрузка статуса с сервера для авторизованных), не запрашиваем suggested questions с page_key — иначе бэкенд ошибочно вернёт вопрос онбординга */
  onboardingStatusLoading?: boolean;
  /** По клику на вопрос онбординга — запуск тура онбординга (не отправлять сообщение в чат) */
  onStartOnboarding?: () => void;
  /** Внешний prompt для немедленной отправки в чат, например из урока */
  externalPrompt?: { id: string; text: string } | null;
  /** Вызывается при попытке отправить сообщение без авторизации (родитель может показать toast) */
  onAuthRequired?: () => void;
  /** Парящий инпут: визуально приподнятая карточка с тенью (стиль ChatGPT) */
  floatingInput?: boolean;
}

export const ChatCore = ({
  contextType,
  user,
  onClose,
  embedded = false,
  showCloseButton = true,
  hideHeader = false,
  courseSlug,
  courseTitle,
  modulesMetadata = [],
  lessonId,
  lessonTitle,
  lessonContent,
  taskId,
  taskTitle,
  userCode,
  queryMetrics,
  containerClassName,
  headerSubtitle,
  enableFeedback = false,
  showChatHistory = true,
  showChatHistoryInEmbedded = false,
  onMinWidthChange,
  onBeforeSendMessage,
  tasksMetadata,
  onApplyTaskSelection,
  initialQuestionsSource,
  onboardingPageKey,
  onboardingCompletedPages,
  onboardingStatusLoading = false,
  onStartOnboarding,
  externalPrompt,
  onAuthRequired,
  floatingInput = false,
}: ChatCoreProps) => {
  const { refreshUser } = useUser();
  const welcomeMessage = WELCOME_BY_CONTEXT[contextType];

  // currentChatId теперь хранит session_id из БД (UUID), а не локальный chat-XXXX
  const [currentChatId, setCurrentChatId] = useState<string>(() => {
    // При первом рендере берём последний открытый session_id из localStorage (тот же ключ, что и на сервере после синка)
    return getLastOpenedSessionId(contextType, courseSlug, lessonId, taskId) ?? "";
  });
  const [chatList, setChatList] = useState<ChatListItem[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([{ role: "assistant", content: welcomeMessage }]);
  const [chatHistoryLoading, setChatHistoryLoading] = useState(false);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [currentStage, setCurrentStage] = useState<"analysis" | "research" | "generation" | "validating" | "generating_sql" | null>(null);
  const [initialQuestions, setInitialQuestions] = useState<string[]>(() => []);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState<Record<number, "good" | "bad">>({});
  const [feedbackDislikeMessageIndex, setFeedbackDislikeMessageIndex] = useState<number | null>(null);
  const [feedbackDislikeReason, setFeedbackDislikeReason] = useState("");
  const [feedbackDislikeComment, setFeedbackDislikeComment] = useState("");
  const [editingUserIndex, setEditingUserIndex] = useState<number | null>(null);
  const [showLimitReachedWarning, setShowLimitReachedWarning] = useState(false);
  const [pendingMessageAfterLimit, setPendingMessageAfterLimit] = useState<string | null>(null);
  const [recentQuestions, setRecentQuestions] = useState<string[]>(() => getRecentQuestions());
  const [recentQuestionsExpanded, setRecentQuestionsExpanded] = useState(false);
  const pendingSendAfterLimitRef = useRef<string | null>(null);
  const pendingExternalPromptRef = useRef<string | null>(null);
  const handledExternalPromptIdRef = useRef<string | null>(null);
  const activeGroup = contextToGroup(contextType);
  const [selectedGroup, setSelectedGroup] = useState<ChatGroupKey | null>(null);
  const pendingQuizRef = useRef<QuizData | null>(null);
  const pendingSuggestedQuestionsRef = useRef<string[] | null>(null);
  const [debugMode, setDebugMode] = useState(() => {
    if (!CAN_DEBUG_PROMPTS || typeof window === "undefined") return false;
    try {
      const p = new URLSearchParams(window.location.search);
      const v = p.get("debug_rag");
      return v === "1" || v === "true";
    } catch {
      return false;
    }
  });
  const [showAuthNotice, setShowAuthNotice] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [showProfileContextInvite, setShowProfileContextInvite] = useState(false);
  const [debugModalMessage, setDebugModalMessage] = useState<ChatMessage | null>(null);
  const pendingDebugRef = useRef<ChatMessage["debug"] | null>(null);


  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const hasScrolledInitial = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const currentChatIdRef = useRef<string>(currentChatId);
  const streamingRef = useRef<{ chatId: string; baseMessages: ChatMessage[]; assistantIndex: number } | null>(null);
  /** Не вызывать touchChat при первом срабатывании эффекта после переключения чата (чтобы выбор чата не двигал его наверх). */
  const justSwitchedToChatIdRef = useRef<string | null>(null);
  /** Если пользователь задал вопрос, который должен предлагать онбординг, привяжем это к следующему ответу ассистента. */
  const pendingOnboardingAfterAnswerRef = useRef<{ pageKey?: string; sourceQuestion: string } | null>(null);
  /** Метаданные для последних follow-up вопросов (после ответа ассистента). */
  const lastFollowUpMetaRef = useRef<QuestionsMeta | null>(null);
  /** Пользователь сам ушёл от низа — не подтягивать скролл при стриминге, чтобы не было фризов. */
  const userScrolledAwayRef = useRef(false);

  useEffect(() => {
    currentChatIdRef.current = currentChatId;
  }, [currentChatId]);

  // Загрузка списка чатов из API при маунте (только для авторизованных)
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    const load = async () => {
      try {
        const list = await fetchAllChatList(API_HOST, user.id);
        if (cancelled) return;
        setChatList(list);
        const targetSession = selectSessionForPageContext(
          list,
          contextType,
          courseSlug,
          lessonId,
          taskId != null ? taskId : null,
        );
        if (targetSession) {
          setCurrentChatId(targetSession);
          setSessionId(targetSession);
          if (contextType === "lesson" && courseSlug && lessonId) {
            setLastOpenedSessionId("lesson", targetSession, courseSlug, lessonId);
          }
          // Загружаем историю
          setChatHistoryLoading(true);
          try {
            const history = await loadChatHistory(API_HOST, targetSession, welcomeMessage);
            if (!cancelled) setMessages(history);
          } catch {
            if (!cancelled) setMessages([{ role: "assistant", content: welcomeMessage }]);
          } finally {
            if (!cancelled) setChatHistoryLoading(false);
          }
        }
      } catch (e) {
        console.warn("[ChatCore] Failed to load chat list from API:", e);
      }
    };
    load();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, contextType, courseSlug, lessonId, String(taskId)]);

  /** Автоопределение режима в editor: optimize при запросе оптимизации */
  const resolveMode = useCallback(
    (message: string, effectiveContext: ChatContextType = contextType) => {
      if (effectiveContext !== "editor") return "explainer";
      const m = message.toLowerCase().trim();
      if (/оптимизир|ускор|медленн|производитель|optimize|performance|slow/.test(m)) return "optimize";
      if (queryMetrics && m.length <= 25 && /^(помоги|оптимизируй|что не так|почему|improve)\s*[?!.]*$/i.test(m))
        return "optimize";
      return "explainer";
    },
    [contextType, queryMetrics]
  );

  const [questionsVersion, setQuestionsVersion] = useState(0);
  // followUpQuestionsLoading теперь всегда false — suggested questions приходят inline из SSE
  const followUpQuestionsLoading = false;
  const [onboardingQuestion, setOnboardingQuestion] = useState<{ text: string; page_key: string } | null>(null);
  const [initialQuestionsMeta, setInitialQuestionsMeta] = useState<QuestionsMeta | null>(null);
  const contextRef = useRef({ lessonId, taskId });
  contextRef.current = { lessonId, taskId };

  const fetchSuggestedQuestions = useCallback(
    async (opts: {
      onSuccess: (
        q: string[],
        onboardingQuestion?: { text: string; page_key: string } | null,
        meta?: QuestionsMeta | null
      ) => void;
      onFallback: () => void;
      lastAssistantContent?: string;
      signal?: AbortSignal;
      taskTitle?: string;
      /** Для initial load: source=ai_tutor_page — статичные вопросы из конфига BE (generic) */
      source?: string;
      /** Ключ страницы и завершённые онбординги — для вопроса «Как пользоваться [страница]» (не кэшируем) */
      pageKey?: string;
      onboardingCompleted?: string[];
    }) => {
      const cacheParams: CacheParams = {
        contextType,
        courseSlug,
        lessonId,
        taskId: taskId ?? undefined,
        lessonTitle,
        lessonContentSnippet: lessonContent?.slice(0, 300),
        taskTitle: opts.taskTitle,
        lastAssistantSnippet: opts.lastAssistantContent?.slice(0, 600),
        source: opts.source,
      };
      const cacheKey = buildCacheKey(cacheParams);
      const skipCache = Boolean(opts.pageKey);
      if (!skipCache) {
        const cached = getCached(cacheKey);
        if (cached && cached.questions.length > 0) {
          opts.onSuccess(cached.questions, undefined, cached.meta ?? null);
          return;
        }
      }
      const hasFollowUpSnippet = Boolean(opts.lastAssistantContent?.trim());
      let res: Response;
      if (hasFollowUpSnippet) {
        // POST: snippet в теле — избегаем обрезки URL (лимит ~2–4 KB), чтобы бэкенд получал текст ответа и вызывал LLM
        const body = {
          context_type: contextType,
          course_slug: courseSlug ?? undefined,
          lesson_id: lessonId ?? undefined,
          task_id: taskId != null ? String(taskId) : undefined,
          lesson_title: lessonTitle ?? undefined,
          lesson_content_snippet: lessonContent?.slice(0, 300) ?? undefined,
          last_assistant_message_snippet: opts.lastAssistantContent!.slice(0, 600),
          task_title: opts.taskTitle ?? undefined,
          source: opts.source ?? undefined,
          page_key: opts.pageKey ?? undefined,
          onboarding_completed: opts.onboardingCompleted?.length ? opts.onboardingCompleted.join(",") : undefined,
        };
        res = await fetch(`${API_HOST}/api/ai/suggested-questions`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
          signal: opts.signal,
        });
      } else {
        const params = new URLSearchParams({ context_type: contextType });
        if (courseSlug) params.set("course_slug", courseSlug);
        if (lessonId) params.set("lesson_id", lessonId);
        if (taskId != null) params.set("task_id", String(taskId));
        if (lessonTitle) params.set("lesson_title", lessonTitle);
        if (lessonContent?.length) params.set("lesson_content_snippet", lessonContent.slice(0, 300));
        if (opts.taskTitle) params.set("task_title", opts.taskTitle);
        if (opts.source) params.set("source", opts.source);
        if (opts.pageKey) params.set("page_key", opts.pageKey);
        if (opts.onboardingCompleted?.length) params.set("onboarding_completed", opts.onboardingCompleted.join(","));
        res = await fetch(`${API_HOST}/api/ai/suggested-questions?${params}`, {
          signal: opts.signal,
        });
      }
      try {
        const data = res.ok ? await res.json() : null;
        const hasQuestions = data?.questions && Array.isArray(data.questions) && data.questions.length > 0;
        const hasOnboarding = data?.onboarding_question && typeof data.onboarding_question === "object";
        let meta: QuestionsMeta | null = null;
        if (data?.questions_meta && typeof data.questions_meta === "object") {
          meta = {};
          for (const [rawText, rawMeta] of Object.entries(
            data.questions_meta as Record<string, unknown>
          )) {
            const text = rawText.trim();
            if (!text || !rawMeta || typeof rawMeta !== "object") continue;
            const m = rawMeta as { suggest_onboarding?: unknown; onboarding_page_key?: unknown };
            const entry: { suggestOnboarding?: boolean; onboardingPageKey?: string } = {};
            if (m.suggest_onboarding) entry.suggestOnboarding = true;
            if (typeof m.onboarding_page_key === "string" && m.onboarding_page_key.trim()) {
              entry.onboardingPageKey = m.onboarding_page_key.trim();
            }
            if (Object.keys(entry).length > 0) {
              meta[text] = entry;
            }
          }
          if (Object.keys(meta).length === 0) meta = null;
        }
        if (hasQuestions || hasOnboarding) {
          const questions = hasQuestions
            ? data.questions
            : (DEFAULT_QUESTIONS[contextType] ?? DEFAULT_QUESTIONS.generic);
          if (hasQuestions && !skipCache) setCached(cacheKey, data.questions, meta ?? undefined);
          opts.onSuccess(questions, data?.onboarding_question ?? null, meta);
        } else {
          opts.onFallback();
        }
      } catch (e) {
        if ((e as Error).name !== "AbortError") opts.onFallback();
      }
    },
    [contextType, courseSlug, lessonId, taskId, lessonTitle, lessonContent]
  );

  // Начальная загрузка suggested-questions: без AbortController, флаг cancelled.
  // Иначе при повторном запуске эффекта (deps/Strict Mode) cleanup отменял бы запрос до ответа.
  useEffect(() => {
    if (user?.id && onboardingPageKey && onboardingStatusLoading) {
      setQuestionsLoading(true);
      return;
    }
    setInitialQuestions([]);
    setOnboardingQuestion(null);
    setInitialQuestionsMeta(null);
    setQuestionsLoading(true);
    let cancelled = false;
    const fallback = () => {
      if (!cancelled) setInitialQuestions(DEFAULT_QUESTIONS[contextType] ?? DEFAULT_QUESTIONS.generic);
    };
    const start = Date.now();
    const MIN_SKELETON_MS = 220;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const initialSource =
      contextType === "generic" && initialQuestionsSource === "ai_tutor_page" ? "ai_tutor_page" : undefined;

    fetchSuggestedQuestions({
      taskTitle: taskTitle,
      source: initialSource,
      pageKey: onboardingPageKey,
      onboardingCompleted: onboardingCompletedPages?.length ? onboardingCompletedPages : undefined,
      onSuccess: (q, ob, meta) => {
        const delay = Math.max(0, MIN_SKELETON_MS - (Date.now() - start));
        timeoutId = window.setTimeout(() => {
          if (!cancelled) {
            setInitialQuestions(q);
            setOnboardingQuestion(ob ?? null);
            setInitialQuestionsMeta(meta ?? null);
            setQuestionsVersion((v) => v + 1);
          }
          setQuestionsLoading(false);
        }, delay);
      },
      onFallback: () => {
        const delay = Math.max(0, MIN_SKELETON_MS - (Date.now() - start));
        timeoutId = window.setTimeout(() => {
          if (!cancelled) fallback();
          setQuestionsLoading(false);
        }, delay);
      },
    });

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      setQuestionsLoading(false);
    };
  }, [contextType, courseSlug, lessonId, taskId, lessonTitle, lessonContent, fetchSuggestedQuestions, taskTitle, initialQuestionsSource, onboardingPageKey, onboardingCompletedPages, onboardingStatusLoading, user?.id]);

  /** Один раз за сессию: приглашение заполнить короткую анкету (если профиль ещё не отмечен на сервере) */
  useEffect(() => {
    if (!user?.id || user.user_context_completed_at) return;
    if (wasProfileInviteShownThisSession()) return;
    markProfileInviteShownThisSession();
    setShowProfileContextInvite(true);
  }, [user?.id, user?.user_context_completed_at]);

  // Suggested questions для последнего ответа теперь приходят inline в SSE (type: 'suggested_questions').
  // Follow-up questions useEffect удалён — нет дополнительных LLM-вызовов при навигации.

  // Обновление списка чатов при возврате на страницу (из другого таба/браузера)
  useEffect(() => {
    if (!user?.id) return;
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        fetchAllChatList(API_HOST, user.id)
          .then(setChatList)
          .catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => { document.removeEventListener("visibilitychange", onVisibility); };
  }, [user?.id, contextType, courseSlug, lessonId, taskId]);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  // Загрузка истории чата при переключении (из БД через API)
  const handleSwitchChat = useCallback(
    async (sessionIdToLoad: string) => {
      if (sessionIdToLoad === currentChatId) return;
      hasScrolledInitial.current = false;
      justSwitchedToChatIdRef.current = sessionIdToLoad;
      setCurrentChatId(sessionIdToLoad);
      setSessionId(sessionIdToLoad);
      setFeedbackGiven({});
      const row = chatList.find((c) => c.sessionId === sessionIdToLoad);
      if (row) {
        setLastOpenedSessionId(
          row.contextType,
          sessionIdToLoad,
          row.courseSlug,
          row.lessonId,
          row.taskId != null && row.taskId !== "" ? row.taskId : null,
        );
      } else {
        setLastOpenedSessionId(contextType, sessionIdToLoad, courseSlug, lessonId, taskId);
      }
      setChatHistoryLoading(true);
      try {
        const history = await loadChatHistory(API_HOST, sessionIdToLoad, welcomeMessage);
        if (currentChatIdRef.current === sessionIdToLoad) {
          setMessages(history);
        }
      } catch {
        if (currentChatIdRef.current === sessionIdToLoad) {
          setMessages([{ role: "assistant", content: welcomeMessage }]);
        }
      } finally {
        setChatHistoryLoading(false);
      }
    },
    [currentChatId, welcomeMessage, contextType, courseSlug, lessonId, taskId, chatList]
  );

  // При переключении чата сбрасываем justSwitchedToChatIdRef
  useEffect(() => {
    if (justSwitchedToChatIdRef.current === currentChatId) {
      justSwitchedToChatIdRef.current = null;
    }
  }, [currentChatId]);

  // При открытии страницы или смене чата — сразу показывать конец истории (последние сообщения)
  useLayoutEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  }, [currentChatId]);

  // Скролл к концу при новых сообщениях, если пользователь уже был внизу или идёт загрузка
  useEffect(() => {
    if (userScrolledAwayRef.current) return;
    const end = messagesEndRef.current;
    const container = messagesContainerRef.current;
    if (!end || !container) return;
    const wasNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 80;
    if (wasNearBottom || isLoading) {
      end.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [messages, isLoading]);

  // Дополнительная прокрутка в конец после первого рендера с историей (на случай задержки layout)
  useEffect(() => {
    if (messages.length <= 1 || hasScrolledInitial.current) return;
    hasScrolledInitial.current = true;
    const t = setTimeout(() => {
      const container = messagesContainerRef.current;
      if (container) container.scrollTop = container.scrollHeight;
    }, 100);
    return () => clearTimeout(t);
  }, [messages.length]);

  // Авторасширение textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    const lineHeight = 20;
    ta.style.height = `${Math.min(Math.max(ta.scrollHeight, lineHeight * 2), lineHeight * 6)}px`;
  }, [input]);

  const handleReset = useCallback(() => {
    // Создаём новый пустой чат (session_id будет назначен бэкендом при первом сообщении)
    setCurrentChatId("");
    setSessionId(null);
    setMessages([{ role: "assistant", content: welcomeMessage }]);
    setInput("");
    setEditingUserIndex(null);
    setFeedbackGiven({});
  }, [welcomeMessage]);

  const handleStartNewChatFromLimit = useCallback(() => {
    const toSend = pendingMessageAfterLimit;
    setShowLimitReachedWarning(false);
    setPendingMessageAfterLimit(null);
    setCurrentChatId("");
    setSessionId(null);
    setMessages([{ role: "assistant", content: welcomeMessage }]);
    setEditingUserIndex(null);
    setFeedbackGiven({});
    if (toSend?.trim()) pendingSendAfterLimitRef.current = toSend;
  }, [pendingMessageAfterLimit, welcomeMessage]);

  const handleDeleteChat = useCallback(
    async (sessionIdToDelete: string) => {
      const row = chatList.find((c) => c.sessionId === sessionIdToDelete);
      await deleteChatSessionOnServer(
        API_HOST,
        sessionIdToDelete,
        row?.contextType ?? contextType,
        row?.courseSlug ?? courseSlug,
        row?.lessonId ?? lessonId,
        row?.taskId != null && row.taskId !== "" ? row.taskId : taskId,
      );
      let next: ChatListItem[] = [];
      try {
        if (user?.id) next = await fetchAllChatList(API_HOST, user.id);
      } catch {
        next = chatList.filter((c) => c.sessionId !== sessionIdToDelete);
      }
      setChatList(next);
      if (sessionIdToDelete === currentChatId) {
        const fallback = selectSessionForPageContext(
          next,
          contextType,
          courseSlug,
          lessonId,
          taskId != null ? taskId : null,
        );
        if (fallback) {
          await handleSwitchChat(fallback);
        } else {
          setCurrentChatId("");
          setSessionId(null);
          setMessages([{ role: "assistant", content: welcomeMessage }]);
        }
      }
    },
    [
      currentChatId,
      chatList,
      contextType,
      courseSlug,
      lessonId,
      taskId,
      welcomeMessage,
      handleSwitchChat,
      user?.id,
    ]
  );

  const handleCreateChatInSidebar = useCallback(() => {
    // Создаём новый пустой чат (session_id назначается бэкендом при первом сообщении)
    setCurrentChatId("");
    setSessionId(null);
    setMessages([{ role: "assistant", content: welcomeMessage }]);
    setFeedbackGiven({});
    if (user?.id && onboardingPageKey && onboardingStatusLoading) {
      setQuestionsLoading(true);
      return;
    }
    // Загружаем initial suggested questions
    setQuestionsLoading(true);
    fetchSuggestedQuestions({
      taskTitle: taskTitle,
      source: contextType === "generic" && initialQuestionsSource === "ai_tutor_page" ? "ai_tutor_page" : undefined,
      pageKey: onboardingPageKey,
      onboardingCompleted: onboardingCompletedPages?.length ? onboardingCompletedPages : undefined,
      onSuccess: (q, ob, meta) => {
        setInitialQuestions(q);
        setOnboardingQuestion(ob ?? null);
        setInitialQuestionsMeta(meta ?? null);
        setQuestionsVersion((v) => v + 1);
        setQuestionsLoading(false);
      },
      onFallback: () => {
        setInitialQuestions(DEFAULT_QUESTIONS[contextType] ?? DEFAULT_QUESTIONS.generic);
        setQuestionsLoading(false);
      },
    });
  }, [
    contextType,
    welcomeMessage,
    taskTitle,
    initialQuestionsSource,
    onboardingPageKey,
    onboardingCompletedPages,
    onboardingStatusLoading,
    user?.id,
    fetchSuggestedQuestions,
  ]);

  const sendMessage = useCallback(
    async (userMessage: string, opts?: { replaceFromIndex?: number }) => {
      const text = userMessage.trim();
      if (!text || isLoading) return;
      if (!user) {
        setShowAuthNotice(true);
        onAuthRequired?.();
        return;
      }

      if (onBeforeSendMessage?.(text)) return;

      if (messages.length >= MAX_CHAT_MESSAGES) {
        setPendingMessageAfterLimit(text);
        setShowLimitReachedWarning(true);
        return;
      }

      setInput("");
      setEditingUserIndex(null);
      addRecentQuestion(text);
      setRecentQuestions(getRecentQuestions());

      let baseMessages: ChatMessage[];
      let assistantIndex: number;

      if (opts?.replaceFromIndex != null) {
        const idx = opts.replaceFromIndex;
        baseMessages = [...messages.slice(0, idx), { role: "user" as const, content: userMessage }];
        assistantIndex = idx + 1;
      } else {
        baseMessages = [...messages, { role: "user" as const, content: userMessage }];
        assistantIndex = baseMessages.length;
      }

      const messagesWithAssistantPlaceholder = [...baseMessages, { role: "assistant" as const, content: "" }];
      const streamingChatId = currentChatId;
      streamingRef.current = {
        chatId: streamingChatId,
        baseMessages: messagesWithAssistantPlaceholder,
        assistantIndex,
      };
      if (import.meta.env.DEV) {
        console.debug("[ChatCore] stream started for chatId:", streamingChatId);
      }

      setMessages(messagesWithAssistantPlaceholder);
      setIsLoading(true);
      setCurrentStage("analysis");

      // Определяем, должен ли следующий ответ ассистента предлагать онбординг.
      const normalizedQuestion = (() => {
        const t = text;
        if (!t) return "";
        const trimmed = t.trim();
        if (!trimmed) return "";
        return trimmed.endsWith("?") ? trimmed : `${trimmed}?`;
      })();
      const metasToCheck: Array<QuestionsMeta | null> = [
        initialQuestionsMeta,
        lastFollowUpMetaRef.current,
      ];
      let onboardingForNextAnswer: { pageKey?: string; sourceQuestion: string } | null = null;
      for (const meta of metasToCheck) {
        if (!meta) continue;
        for (const [rawText, info] of Object.entries(meta)) {
          if (!info?.suggestOnboarding) continue;
          const metaText = rawText.trim();
          if (!metaText) continue;
          const normMeta = metaText.endsWith("?") ? metaText : `${metaText}?`;
          if (normMeta === normalizedQuestion) {
            const targetPageKey =
              info.onboardingPageKey || onboardingPageKey || undefined;
            if (targetPageKey) {
              onboardingForNextAnswer = {
                pageKey: targetPageKey,
                sourceQuestion: metaText,
              };
            }
            break;
          }
        }
        if (onboardingForNextAnswer) break;
      }
      // Fallback: на странице задач вопрос «С какой задачи начать?» всегда предлагает онбординг (даже если meta не пришёл).
      if (
        !onboardingForNextAnswer &&
        contextType === "tasklist" &&
        onboardingPageKey === "tasklist" &&
        /^с\s+какой\s+задачи\s+начать\s*\?*$/i.test(normalizedQuestion.replace(/\?+$/, "").trim())
      ) {
        onboardingForNextAnswer = {
          pageKey: "tasklist",
          sourceQuestion: "С какой задачи начать?",
        };
      }
      if (onboardingForNextAnswer) {
        pendingOnboardingAfterAnswerRef.current = onboardingForNextAnswer;
      }

      const activeRow = currentChatId ? chatList.find((c) => c.sessionId === currentChatId) : undefined;
      const streamContextType: ChatContextType =
        (activeRow?.contextType as ChatContextType) || contextType;
      const streamCourseSlug = activeRow?.courseSlug ?? courseSlug;
      const streamLessonId = activeRow?.lessonId ?? lessonId;
      const streamTaskId =
        activeRow?.taskId != null && activeRow.taskId !== ""
          ? Number(activeRow.taskId)
          : taskId;

      const mode = resolveMode(userMessage, streamContextType);
      const body: Record<string, unknown> = {
        message: userMessage,
        mode,
        session_id: sessionId,
        context_type: streamContextType,
        include_debug: CAN_DEBUG_PROMPTS && debugMode,
      };

      if (streamContextType === "course") {
        body.course_slug = streamCourseSlug;
        body.course_title =
          streamCourseSlug && streamCourseSlug === courseSlug ? courseTitle : undefined;
        body.modules_metadata =
          streamCourseSlug && streamCourseSlug === courseSlug ? modulesMetadata : undefined;
      } else if (streamContextType === "lesson") {
        body.lesson_id = streamLessonId;
        body.course_slug = streamCourseSlug;
        body.lesson_title =
          streamLessonId && streamLessonId === lessonId && streamCourseSlug === courseSlug
            ? lessonTitle
            : undefined;
        body.lesson_content =
          streamLessonId && streamLessonId === lessonId && streamCourseSlug === courseSlug
            ? lessonContent
            : undefined;
      } else if (streamContextType === "editor") {
        body.task_id = streamTaskId != null ? String(streamTaskId) : undefined;
        body.task_title = taskTitle;
        body.user_code = userCode;
        if (mode === "optimize" && queryMetrics) {
          body.query_metrics = queryMetrics;
        }
      } else if (streamContextType === "tasklist" && tasksMetadata?.length) {
        body.tasks_metadata = tasksMetadata.map((t) => ({
          id: t.id,
          title: t.title,
          description: t.description,
          domain: t.domain,
          difficulty: t.difficulty,
          tag: t.tag,
        }));
      }
      if (user?.id) body.user_id = user.id;

      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const response = await fetch(`${API_HOST}/api/ai/explainer/stream`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
          signal: controller.signal,
        });

        if (!response.ok) {
          const err = await response.json().catch(() => ({ detail: response.statusText }));
          const detail = err?.detail;
          const msg =
            typeof detail === "string"
              ? detail
              : Array.isArray(detail) && detail[0]?.msg
                ? detail[0].msg
                : detail && typeof detail === "object" && "message" in detail
                  ? String((detail as { message: unknown }).message)
                  : typeof detail === "object" && detail !== null
                    ? JSON.stringify(detail)
                    : response.statusText;
          throw new Error(msg || response.statusText);
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let fullContent = "";
        let sources: ChatMessage["sources"] = [];

        if (!reader) throw new Error("Reader недоступен");

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === "status") {
                if (data.stage === "search_sources") setCurrentStage("analysis");
                else if (data.stage === "sources_found") setCurrentStage("research");
                else if (data.stage === "generation") setCurrentStage("generation");
                else if (data.stage === "generating_sql") setCurrentStage("generating_sql");
                else if (data.stage === "validating") setCurrentStage("validating");
              } else if (data.type === "start" && data.session_id) {
                const newSessionId: string = data.session_id;
                setSessionId(newSessionId);
                setCurrentChatId(newSessionId);
                setCurrentStage("generation");
                setLastOpenedSessionId(contextType, newSessionId, courseSlug, lessonId, taskId);
                // Update streamingRef to use new session_id as chatId
                if (streamingRef.current) {
                  streamingRef.current = { ...streamingRef.current, chatId: newSessionId };
                }
              } else if (data.type === "chunk") {
                fullContent += data.content || "";
                const sr = streamingRef.current;
                if (sr) {
                  const next = [...sr.baseMessages];
                  if (next[sr.assistantIndex]) next[sr.assistantIndex] = { ...next[sr.assistantIndex], content: stripTasklistPayloadLine(fullContent) };
                  if (currentChatIdRef.current === sr.chatId) setMessages(next);
                }
              } else if (data.type === "content_corrected" && data.content) {
                fullContent = data.content;
                const sr = streamingRef.current;
                if (sr) {
                  const next = [...sr.baseMessages];
                  if (next[sr.assistantIndex]) next[sr.assistantIndex] = { ...next[sr.assistantIndex], content: stripTasklistPayloadLine(fullContent) };
                  if (currentChatIdRef.current === sr.chatId) setMessages(next);
                }
              } else if (data.type === "suggested_questions" && Array.isArray(data.questions)) {
                // Сохраняем в ref — применяем в done, чтобы не потерять при следующем chunk
                if (data.questions.length > 0) {
                  pendingSuggestedQuestionsRef.current = data.questions;
                }
              } else if (data.type === "sources") {
                sources = data.sources || [];
              } else if (CAN_DEBUG_PROMPTS && data.type === "debug" && data.system_prompt != null) {
                pendingDebugRef.current = {
                  system_prompt: data.system_prompt ?? "",
                  user_prompt: data.user_prompt ?? "",
                  rag_context: data.rag_context ?? "",
                };
              } else if (data.type === "quiz" && data.question && data.options && typeof data.correct_index === "number") {
                pendingQuizRef.current = {
                  question: data.question,
                  options: data.options,
                  correctIndex: data.correct_index,
                };
              } else if (data.type === "done") {
                const quiz = pendingQuizRef.current;
                pendingQuizRef.current = null;
                const suggestedQuestions = pendingSuggestedQuestionsRef.current;
                pendingSuggestedQuestionsRef.current = null;
                const isError = !fullContent.trim();
                const taskIds = Array.isArray(data.task_ids) ? data.task_ids : undefined;
                const sqlValidated = data.sql_validated === true;
                const doneMessageId: number | undefined = typeof data.message_id === "number" ? data.message_id : undefined;
                const doneTraceId: string | undefined = typeof data.trace_id === "string" ? data.trace_id : undefined;
                const doneTimingMs = (import.meta.env.DEV && data.timing_ms && typeof data.timing_ms === "object")
                  ? data.timing_ms as { rag_ms?: number; llm_stream_ms?: number; sql_validation_ms?: number; total_ms?: number }
                  : undefined;
                const sr = streamingRef.current;
                const debugPayload = pendingDebugRef.current;
                if (debugPayload) pendingDebugRef.current = null;
                if (sr) {
                  const next = [...sr.baseMessages];
                  if (next[sr.assistantIndex]) {
                    const base: ChatMessage = {
                      ...next[sr.assistantIndex],
                      content: stripTasklistPayloadLine(fullContent),
                      sources,
                      quiz: quiz ?? undefined,
                      suggestedQuestions: suggestedQuestions ?? next[sr.assistantIndex].suggestedQuestions,
                      isError: isError || undefined,
                      taskIds,
                      debug: debugPayload ?? undefined,
                      sqlValidated: sqlValidated || undefined,
                      messageId: doneMessageId ?? next[sr.assistantIndex].messageId,
                      traceId: doneTraceId ?? next[sr.assistantIndex].traceId,
                      timingMs: doneTimingMs ?? next[sr.assistantIndex].timingMs,
                    };
                    // Привязываем онбординг к ответу, если предыдущий вопрос был «онбординг-триггером».
                    const pendingOnboarding = pendingOnboardingAfterAnswerRef.current;
                    let onboardingSuggestion: ChatMessage["onboardingSuggestion"] | undefined;
                    if (pendingOnboarding) {
                      const targetPageKey =
                        pendingOnboarding.pageKey || onboardingPageKey || undefined;
                      if (targetPageKey) {
                        onboardingSuggestion = {
                          pageKey: targetPageKey,
                          sourceQuestion: pendingOnboarding.sourceQuestion,
                        };
                      }
                    }
                    if (onboardingSuggestion) {
                      next[sr.assistantIndex] = { ...base, onboardingSuggestion };
                    } else {
                      next[sr.assistantIndex] = base;
                    }
                    // Сбрасываем pending-флаг, чтобы он не применился к следующему ответу.
                    pendingOnboardingAfterAnswerRef.current = null;
                  }
                  if (currentChatIdRef.current === sr.chatId) {
                    setMessages(next);
                  }
                  // Обновляем список чатов из API
                  if (user?.id) {
                    fetchAllChatList(API_HOST, user.id)
                      .then(setChatList)
                      .catch(() => {});
                  }
                }
                // suggested questions уже пришли inline в событии type='suggested_questions' выше
              } else if (data.type === "error") {
                throw new Error(data.error || "Ошибка");
              }
            } catch (e) {
              if (e instanceof Error && e.message !== "Ошибка") console.error("Parse error:", e);
            }
          }
        }
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          return;
        }
        let msg: string;
        if (error instanceof Error) {
          msg = error.message;
        } else if (error && typeof error === "object" && "message" in error) {
          msg = String((error as { message: unknown }).message);
        } else {
          msg = typeof error === "string" ? error : "Неизвестная ошибка";
        }
        if (msg === "[object Object]") msg = "Неизвестная ошибка";
        const sr = streamingRef.current;
        if (sr) {
          const next = [...sr.baseMessages];
          if (next[sr.assistantIndex])
            next[sr.assistantIndex] = { ...next[sr.assistantIndex], content: `Ошибка: ${msg}`, isError: true };
          if (currentChatIdRef.current === sr.chatId) setMessages(next);
        }
      } finally {
        abortControllerRef.current = null;
        const finishedChatId = streamingRef.current?.chatId;
        streamingRef.current = null;
        pendingQuizRef.current = null;
        pendingSuggestedQuestionsRef.current = null;
        userScrolledAwayRef.current = false;
        if (import.meta.env.DEV && finishedChatId) {
          console.debug("[ChatCore] stream finished for chatId:", finishedChatId);
        }
        setIsLoading(false);
        setCurrentStage(null);
      }
    },
    [
      user,
      onAuthRequired,
      isLoading,
      messages,
      sessionId,
      contextType,
      resolveMode,
      courseSlug,
      courseTitle,
      modulesMetadata,
      lessonId,
      lessonTitle,
      lessonContent,
      taskId,
      taskTitle,
      userCode,
      queryMetrics,
      onBeforeSendMessage,
      onboardingPageKey,
      onboardingCompletedPages,
      chatList,
      currentChatId,
      tasksMetadata,
    ]
  );

  useEffect(() => {
    if (
      pendingSendAfterLimitRef.current &&
      messages.length === 1 &&
      messages[0].role === "assistant" &&
      !isLoading
    ) {
      const msg = pendingSendAfterLimitRef.current;
      pendingSendAfterLimitRef.current = null;
      setTimeout(() => sendMessage(msg), 0);
    }
  }, [messages.length, messages[0]?.role, isLoading, sendMessage]);

  useEffect(() => {
    if (!externalPrompt?.id || !externalPrompt?.text?.trim()) return;
    if (handledExternalPromptIdRef.current === externalPrompt.id) return;
    handledExternalPromptIdRef.current = externalPrompt.id;
    pendingExternalPromptRef.current = externalPrompt.text.trim();
  }, [externalPrompt]);

  useEffect(() => {
    if (!pendingExternalPromptRef.current || isLoading) return;
    const msg = pendingExternalPromptRef.current;
    pendingExternalPromptRef.current = null;
    setTimeout(() => sendMessage(msg), 0);
  }, [isLoading, sendMessage]);

  const handleSend = () => {
    if (!input.trim() || isLoading) return;
    sendMessage(input.trim(), editingUserIndex != null ? { replaceFromIndex: editingUserIndex } : undefined);
  };

  const showInitialQuestions = messages.length <= 1 && !isLoading;

  /** Мета для вопроса: ключ в API может быть с "?" или без — проверяем оба варианта. */
  const getMetaForQuestion = useCallback((questionText: string, meta: QuestionsMeta | null) => {
    if (!meta || !questionText) return undefined;
    const t = questionText.trim();
    return meta[t] ?? meta[t.endsWith("?") ? t.slice(0, -1) : `${t}?`];
  }, []);

  /** Вопросы для сообщения: из suggestedQuestions или парсинг из контента */
  const getQuestionsForMessage = useCallback((msg: ChatMessage) => {
    if (msg.suggestedQuestions && msg.suggestedQuestions.length > 0) return msg.suggestedQuestions;
    const { questions } = extractFollowUpQuestions(msg.content);
    return questions;
  }, []);

  /** Отвеченные вопросы для сообщения idx: следующий user message — это кликнутый вопрос */
  const getAnsweredForMessage = useCallback(
    (idx: number, questions: string[]) => {
      const next = messages[idx + 1];
      if (next?.role !== "user") return new Set<string>();
      const sent = next.content.trim();
      const normalize = (s: string) => (s.endsWith("?") ? s : s + "?");
      const match = questions.find((q) => normalize(q) === normalize(sent) || q === sent);
      return match ? new Set([match]) : new Set<string>();
    },
    [messages]
  );

  const sendFeedback = useCallback(
    async (
      idx: number,
      rating: "good" | "bad",
      correction?: string,
      feedbackReason?: string
    ) => {
      const userMsg = idx > 0 ? messages[idx - 1] : null;
      if (userMsg?.role !== "user" || !messages[idx]?.content?.trim()) return;
      const assistantContent = messages[idx].content || "";
      setFeedbackGiven((prev) => ({ ...prev, [idx]: rating }));
      setFeedbackDislikeMessageIndex(null);
      try {
        await fetch(`${API_HOST}/api/ai/feedback`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_message: userMsg.content,
            assistant_message: correction ?? assistantContent,
            context_type: contextType,
            mode: resolveMode(userMsg.content),
            rating,
            correction: correction || undefined,
            feedback_reason: feedbackReason || undefined,
            sources: rating === "bad" ? messages[idx].sources : undefined,
            lesson_id: lessonId,
            course_slug: courseSlug,
            lesson_title: lessonTitle,
            task_id: taskId != null ? String(taskId) : undefined,
            task_title: taskTitle,
          }),
        });
        const feedbackToSave = { rating, feedback_reason: feedbackReason };
        setMessages((prev) => {
          const next = [...prev];
          next[idx] = { ...next[idx], feedback: feedbackToSave };
          return next;
        });
      } catch (e) {
        console.warn("[ChatCore] Feedback send failed:", e);
      }
    },
    [
      messages,
      currentChatId,
      contextType,
      resolveMode,
      lessonId,
      courseSlug,
      lessonTitle,
      taskId,
      taskTitle,
    ]
  );

  const placeholder = editingUserIndex != null ? "Редактирование вопроса…" : "Например: с чего начать?";

  const isMobile = useMobile();
  /** На странице курса в боковой панели на мобильном показываем настройки и сброс иконками, как на десктопе (не только в ⋯). */
  const inlineSettingsResetInHeader = !isMobile || (embedded && contextType === "course");
  const withSidebar = showChatHistory && (!embedded || showChatHistoryInEmbedded);
  /** На мобильном embedded сайдбар в drawer, в потоке не рендерим */
  const showSidebarInline = withSidebar && !(embedded && isMobile);
  const showMobileSidebarButton = withSidebar && embedded && isMobile;
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const sidebarExpanded = withSidebar && selectedGroup !== null;
  const sidebarWidth = sidebarExpanded ? 200 : 56;
  const totalWidth = showSidebarInline ? 380 + sidebarWidth : 380;

  const defaultContainerClass = embedded
    ? "bg-white border border-neutral-border rounded-xl shadow-sm flex flex-col h-full min-h-[400px] overflow-hidden"
    : "h-full w-[380px] min-w-[380px] bg-white border-l border-neutral-border shadow-xl flex flex-col";
  const containerClass = containerClassName ?? defaultContainerClass;

  // embedded: весь чат расширяется вместе с sidebar. max-w-full + overflow-x-hidden чтобы на мобилке контент не вылезал вправо.
  const outerClass = embedded
    ? "flex flex-1 min-w-0 max-w-full min-h-0 overflow-hidden overflow-x-hidden font-sans"
    : showSidebarInline
      ? "fixed top-0 right-0 h-full z-50 flex font-sans"
      : "fixed top-0 right-0 h-full w-[380px] bg-white border-l border-neutral-border shadow-xl z-50 flex flex-col font-sans";

  const outerStyle = showSidebarInline
    ? {
        minWidth: totalWidth,
        width: "100%",
        flexShrink: 0,
        flexGrow: 1,
      }
    : undefined;

  // Уведомляем родителя о требуемой ширине (embedded: aside должен расширяться)
  useEffect(() => {
    if (embedded && showSidebarInline && onMinWidthChange) {
      onMinWidthChange(totalWidth);
    }
  }, [embedded, showSidebarInline, totalWidth, onMinWidthChange]);

  const headerPadding = embedded ? "p-3" : "p-4";
  const headerTitleSize = embedded ? "text-base" : "text-lg";
  const contentPadding = embedded ? "p-3" : "p-4";

  return (
    <div className={outerClass} style={outerStyle}>
      <div className={(showSidebarInline ? "flex-1 flex flex-col min-w-0 max-w-full overflow-x-hidden " : embedded ? "max-w-full overflow-x-hidden " : "") + containerClass}>
      {(showMobileSidebarButton && hideHeader) && (
        <div className="flex items-center justify-end border-b border-neutral-border flex-shrink-0 p-2">
          <button
            type="button"
            onClick={() => setMobileSidebarOpen(true)}
            className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-neutral-light transition-colors text-neutral-text/60 hover:text-neutral-text focus:outline-none focus:ring-2 focus:ring-primary/30"
            aria-label="История чатов"
            title="История чатов (группы и диалоги)"
          >
            <PanelLeftOpen className="w-4 h-4" />
          </button>
        </div>
      )}
      {!hideHeader && (
      <div
        className={`flex items-center justify-between border-b border-neutral-border flex-shrink-0 ${headerPadding}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <MessageCircle className="w-5 h-5 text-primary flex-shrink-0" />
          <div className="flex flex-col min-w-0">
            <h2 className={`${headerTitleSize} font-heading font-semibold text-neutral-text`}>AI Tutor</h2>
            {headerSubtitle && (
              <span className="text-xs text-neutral-text/70 truncate">{headerSubtitle}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {showMobileSidebarButton && (
            <button
              type="button"
              onClick={() => setMobileSidebarOpen(true)}
              className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-neutral-light transition-colors text-neutral-text/60 hover:text-neutral-text focus:outline-none focus:ring-2 focus:ring-primary/30"
              aria-label="История чатов"
              title="История чатов (группы и диалоги)"
            >
              <PanelLeftOpen className="w-4 h-4" />
            </button>
          )}
          {/* Настройки и сброс: на десктопе и в embedded-чате курса на мобильном — отдельные иконки */}
          {inlineSettingsResetInHeader && user && (
            <button
              type="button"
              onClick={() => setSettingsOpen(true)}
              className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-neutral-light transition-colors text-neutral-text/60 hover:text-neutral-text focus:outline-none focus:ring-2 focus:ring-primary/30"
              aria-label="Настройки контекста"
              title="Настройки (опрос о себе для тьютора)"
            >
              <Sliders className="w-4 h-4" />
            </button>
          )}
          {inlineSettingsResetInHeader && (
            <button
              onClick={handleReset}
              className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-neutral-light transition-colors text-neutral-text/60 hover:text-neutral-text focus:outline-none focus:ring-2 focus:ring-primary/30"
              aria-label="Сбросить историю"
              title="Сбросить диалог"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          )}
          {/* Mobile: меню ⋯, если настройки/сброс не вынесены в шапку */}
          {isMobile && !inlineSettingsResetInHeader && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setMobileMenuOpen((v) => !v)}
                className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-neutral-light transition-colors text-neutral-text/60 hover:text-neutral-text focus:outline-none focus:ring-2 focus:ring-primary/30"
                aria-label="Дополнительные действия"
                title="Ещё"
              >
                <MoreHorizontal className="w-4 h-4" />
              </button>
              {mobileMenuOpen && (
                <div
                  className="absolute top-full right-0 mt-1 w-48 bg-white rounded-xl border border-neutral-border shadow-lg z-50 py-1 overflow-hidden"
                  onMouseLeave={() => setMobileMenuOpen(false)}
                >
                  {user && (
                    <button
                      type="button"
                      onClick={() => { setSettingsOpen(true); setMobileMenuOpen(false); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-neutral-text hover:bg-neutral-light transition-colors"
                    >
                      <Sliders className="w-4 h-4 text-neutral-text/50" />
                      Настройки
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => { handleReset(); setMobileMenuOpen(false); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-neutral-text hover:bg-neutral-light transition-colors"
                  >
                    <RotateCcw className="w-4 h-4 text-neutral-text/50" />
                    Сбросить диалог
                  </button>
                </div>
              )}
            </div>
          )}
          {showCloseButton && (
            <button
              onClick={onClose}
              className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-neutral-light transition-colors text-neutral-text/60 hover:text-neutral-text focus:outline-none focus:ring-2 focus:ring-primary/30"
              aria-label="Закрыть AI Tutor"
              title="Закрыть AI Tutor"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
      )}

      <div className="flex-1 min-h-0 min-w-0 flex flex-col overflow-hidden">
        {showLimitReachedWarning && (
          <div className="flex-shrink-0 border-b border-amber-200 bg-amber-50 px-4 py-3 flex items-center justify-between gap-3">
            <p className="text-sm text-amber-800">
              Достигнут лимит сообщений ({MAX_CHAT_MESSAGES}). Для продолжения создаётся новый чат.
            </p>
            <button
              type="button"
              onClick={handleStartNewChatFromLimit}
              className="flex-shrink-0 px-3 py-2 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 transition"
            >
              Создать новый чат
            </button>
          </div>
        )}
        <div
          ref={messagesContainerRef}
          className={`flex-1 min-w-0 w-full max-w-full overflow-y-auto overflow-x-hidden space-y-4 box-border ${contentPadding} pl-4 pr-4 [scrollbar-width:thin] [scrollbar-color:rgba(148,163,184,0.28)_transparent] [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-300/35 hover:[&::-webkit-scrollbar-thumb]:bg-slate-400/45`}
          data-ai-chat-scroll
          onScroll={() => {
            const container = messagesContainerRef.current;
            if (!container) return;
            const distanceFromBottom =
              container.scrollHeight - container.scrollTop - container.clientHeight;
            if (distanceFromBottom > 80) {
              userScrolledAwayRef.current = true;
            } else {
              userScrolledAwayRef.current = false;
            }
          }}
        >
          {messages.map((msg, idx) => (
            <div key={idx} className={`flex min-w-0 w-full ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`w-full min-w-0 max-w-full rounded-xl px-4 py-2.5 break-words text-sm overflow-x-hidden ${
                  msg.role === "user"
                    ? "bg-neutral-light text-neutral-text border border-neutral-border/60 flex items-center min-h-[44px]"
                    : msg.isError
                      ? "bg-red-50 text-red-800 border border-red-200"
                      : "bg-white text-neutral-text border border-neutral-border/60"
                }`}
              >
                {msg.role === "assistant" ? (
                  <div className="flex flex-col gap-1.5">
                    <div className="text-sm min-w-0 overflow-hidden">
                      {idx === messages.length - 1 && !msg.content?.trim() && isLoading ? (
                        <AITutorTypingIndicator stage={currentStage} />
                      ) : msg.content?.trim() ? (
                        <AITutorMessageContent
                          content={msg.content}
                          user={user}
                          extractFollowUps
                          codeBlocksPlaceholder={idx === messages.length - 1 && isLoading}
                          loadingStage={idx === messages.length - 1 ? currentStage : undefined}
                          tasklistPlaceholder={contextType === "tasklist" && idx === messages.length - 1 && isLoading}
                          sqlValidated={msg.sqlValidated}
                        />
                      ) : (
                        <p className="text-sm text-neutral-text/70">Не удалось получить ответ. Измените формулировку или попробуйте позже.</p>
                      )}
                    </div>
                    {CAN_DEBUG_PROMPTS && msg.debug && (
                      <div className="mt-1">
                        <button
                          type="button"
                          onClick={() => setDebugModalMessage(msg)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-neutral-text/80 bg-neutral-light hover:bg-neutral-border/40 border border-neutral-border/60 rounded-lg transition-colors"
                        >
                          <Terminal className="w-3.5 h-3.5" />
                          Показать отладку
                        </button>
                      </div>
                    )}
                    {(msg.sources?.length > 0 || msg.taskIds?.length || msg.onboardingSuggestion || msg.messageId || msg.traceId) && (
                      <div className="mt-2 pt-2 border-t border-neutral-border/50">
                        {(msg.taskIds?.length || msg.onboardingSuggestion) && (
                          <div className="flex flex-wrap items-center gap-2 mb-1.5">
                            {msg.taskIds && msg.taskIds.length > 0 && onApplyTaskSelection && (
                              <button
                                type="button"
                                onClick={() => onApplyTaskSelection(msg.taskIds!)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary bg-white border border-primary/40 rounded-lg hover:bg-primary/10 hover:border-primary/50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30"
                              >
                                <Filter className="w-4 h-4" />
                                Показать подборку ({msg.taskIds.length} {msg.taskIds.length === 1 ? "задачу" : msg.taskIds.length < 5 ? "задачи" : "задач"})
                              </button>
                            )}
                            {msg.onboardingSuggestion && onStartOnboarding && (
                              <button
                                type="button"
                                onClick={() => onStartOnboarding()}
                                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-neutral-text bg-white border-2 border-primary/30 rounded-lg hover:bg-primary/5 hover:border-primary/50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30"
                              >
                                <BookOpen className="w-4 h-4 text-primary" />
                                Пройти онбординг по этой странице
                              </button>
                            )}
                          </div>
                        )}
                        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                          {msg.sources && msg.sources.length > 0 && (
                            <AITutorSources sources={msg.sources} showEmptyMessage={false} variant="inline-list" />
                          )}
                          {(msg.traceId || msg.messageId) && (
                            <span
                              className="shrink-0 text-xs text-neutral-text/30 cursor-pointer select-all hover:text-neutral-text/50 transition-colors font-mono"
                              title={msg.traceId ? `trace: ${msg.traceId} (нажмите, чтобы скопировать)` : "Нажмите, чтобы скопировать ID сообщения"}
                              onClick={() => navigator.clipboard.writeText(msg.traceId ?? String(msg.messageId))}
                            >
                              #{msg.traceId ? msg.traceId.slice(0, 8) : msg.messageId}
                            </span>
                          )}
                          {import.meta.env.DEV && msg.timingMs && (
                            <span
                              className="shrink-0 text-xs text-neutral-text/25 font-mono select-none"
                              title={[
                                `RAG: ${msg.timingMs.rag_ms ?? "?"}ms`,
                                `LLM: ${msg.timingMs.llm_stream_ms ?? "?"}ms`,
                                `SQL: ${msg.timingMs.sql_validation_ms ?? "?"}ms`,
                              ].join(" · ")}
                            >
                              ⏱ {msg.timingMs.total_ms != null ? `${(msg.timingMs.total_ms / 1000).toFixed(1)}s` : "?"}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    {(() => {
                      if (msg.isError) return null;
                      const lastAssistantIdx = (() => {
                        for (let i = messages.length - 1; i >= 0; i--) if (messages[i].role === "assistant") return i;
                        return -1;
                      })();
                      const isLoadingFollowUp = idx === lastAssistantIdx && followUpQuestionsLoading;
                      const questions = getQuestionsForMessage(msg);
                      if (questions.length === 0 && !isLoadingFollowUp) return null;

                      return (
                        <FollowUpSuggestionsBlock
                          questions={questions}
                          isLoadingFollowUp={isLoadingFollowUp}
                          onSend={sendMessage}
                          getAnswered={() => getAnsweredForMessage(idx, questions)}
                        />
                      );
                    })()}
                    {msg.quiz && (
                      <QuizCard
                        question={msg.quiz.question}
                        options={msg.quiz.options}
                        correctIndex={msg.quiz.correctIndex}
                        initialSelectedIndex={msg.quiz.userAnswerIndex ?? null}
                        onAnswer={(_correct, selectedIndex) => {
                          if (msg.quiz?.quizId && sessionId) {
                            void saveQuizAnswer(API_HOST, sessionId, msg.quiz.quizId, selectedIndex);
                          }
                        }}
                      />
                    )}
                    {(enableFeedback || msg.isError) && idx > 0 && !isLoading && (
                      <div className="mt-2 pt-2 border-t border-neutral-border/40 flex flex-col gap-1.5">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-1">
                        {msg.isError && (
                          <button
                            type="button"
                            onClick={() => {
                              const userIdx = idx - 1;
                              if (userIdx >= 0 && messages[userIdx]?.role === "user") {
                                sendMessage(messages[userIdx].content, { replaceFromIndex: userIdx });
                              }
                            }}
                            className="flex items-center gap-1.5 text-xs px-2 py-1.5 rounded hover:bg-neutral-light transition text-primary"
                            title="Повторить запрос"
                          >
                            <RefreshCw className="w-3.5 h-3.5" />
                            Повторить
                          </button>
                        )}
                        {enableFeedback && msg.content?.trim() && (
                          <>
                            <button
                              type="button"
                              onClick={() => sendFeedback(idx, "good")}
                              className={`min-w-[44px] min-h-[44px] flex items-center justify-center rounded hover:bg-neutral-light transition ${
                                (feedbackGiven[idx] ?? msg.feedback?.rating) === "good" ? "text-green-600 bg-green-50" : "text-neutral-text/50 hover:text-neutral-text"
                              }`}
                              title="Хороший ответ"
                              aria-label="Хороший ответ"
                            >
                              <ThumbsUp className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setFeedbackDislikeMessageIndex(idx);
                                setFeedbackDislikeReason("");
                                setFeedbackDislikeComment("");
                              }}
                              className={`min-w-[44px] min-h-[44px] flex items-center justify-center rounded hover:bg-neutral-light transition ${
                                (feedbackGiven[idx] ?? msg.feedback?.rating) === "bad" ? "text-red-600 bg-red-50" : "text-neutral-text/50 hover:text-neutral-text"
                              }`}
                              title={msg.feedback?.rating === "bad" && msg.feedback?.feedback_reason ? `Плохой ответ. Причина: ${msg.feedback.feedback_reason}` : "Плохой ответ"}
                              aria-label="Плохой ответ"
                            >
                              <ThumbsDown className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                        </div>
                        {msg.content?.trim() && (
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(msg.content || "").catch(() => {});
                            }}
                            className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded hover:bg-neutral-light transition text-neutral-text/50 hover:text-neutral-text ml-auto shrink-0"
                            title="Копировать"
                            aria-label="Копировать ответ"
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                        )}
                        </div>
                        {msg.feedback?.rating === "bad" && msg.feedback?.feedback_reason && (
                          <p className="text-xs text-neutral-text/70" title="Причина дизлайка">
                            Причина: {msg.feedback.feedback_reason}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="group flex items-center gap-1 w-full">
                    <p className="whitespace-pre-wrap text-sm flex-1 min-w-0">{msg.content}</p>
                    <button
                      type="button"
                      onClick={() => {
                        setEditingUserIndex(idx);
                        setInput(msg.content || "");
                        textareaRef.current?.focus();
                      }}
                      className="opacity-0 group-hover:opacity-100 min-w-[32px] min-h-[32px] flex items-center justify-center rounded hover:bg-neutral-light transition text-neutral-text/50 hover:text-neutral-text shrink-0"
                      title="Изменить вопрос"
                      aria-label="Изменить вопрос"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {showInitialQuestions && (
          <div className={`pb-3 flex-shrink-0 ${contentPadding}`}>
            <p className="text-xs font-medium text-neutral-text/60 mb-2 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" />
              Попробуйте спросить
            </p>
            <AnimatePresence mode="wait">
              {questionsLoading ? (
                <motion.div
                  key="skeleton"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex flex-wrap gap-2"
                >
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="h-11 rounded-lg bg-neutral-light/80 animate-pulse"
                      style={{ width: `${60 + (i % 3) * 40}px` }}
                    />
                  ))}
                </motion.div>
              ) : (() => {
                  const hasNoUserMessages = messages.filter((m) => m.role === "user").length === 0;
                  const showOnboarding =
                    onboardingQuestion && hasNoUserMessages && onStartOnboarding;
                  const questionsToShow =
                    showOnboarding && onboardingQuestion
                      ? [
                          onboardingQuestion.text,
                          ...initialQuestions.filter((x) => x !== onboardingQuestion!.text),
                        ]
                      : initialQuestions;
                  return questionsToShow.length > 0 ? (
                    <motion.div
                      key={questionsVersion}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="flex flex-wrap gap-2"
                    >
                      {questionsToShow.map((q, i) => {
                        const isOnboarding = showOnboarding && q === onboardingQuestion?.text;
                        const metaForQ = getMetaForQuestion(q, initialQuestionsMeta);
                        const pageKeyForQ =
                          metaForQ?.onboardingPageKey || onboardingPageKey || undefined;
                        const canSuggestOnboardingLater =
                          !!metaForQ?.suggestOnboarding && !!pageKeyForQ && !!onStartOnboarding;
                        return (
                          <motion.button
                            key={`${questionsVersion}-${i}`}
                            type="button"
                            onClick={() => {
                              if (isOnboarding) {
                                onStartOnboarding?.();
                                return;
                              }
                              if (canSuggestOnboardingLater) {
                                pendingOnboardingAfterAnswerRef.current = {
                                  pageKey: pageKeyForQ,
                                  sourceQuestion: q,
                                };
                              } else {
                                // Если вопрос не помечен в конфиге — не привязываем онбординг к следующему ответу.
                                pendingOnboardingAfterAnswerRef.current = null;
                              }
                              sendMessage(q);
                            }}
                            className={`text-xs px-2.5 py-2 min-h-[44px] rounded-lg border text-left transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30 ${
                              isOnboarding
                                ? "border-primary bg-primary/10 text-primary hover:bg-primary/15"
                                : "border-neutral-border bg-white hover:border-primary/40 hover:bg-primary/5 text-neutral-text"
                            }`}
                            initial={{ opacity: 0, y: 4 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.05, duration: 0.2 }}
                          >
                            {q}
                          </motion.button>
                        );
                      })}
                    </motion.div>
                  ) : null;
                })()}
            </AnimatePresence>
          </div>
        )}

        {!user ? (
          <div className={`pb-3 flex-shrink-0 ${contentPadding}`}>
            {showAuthNotice ? (
              <div className="flex items-center justify-between gap-2 text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-4 py-3">
                <span>Авторизуйтесь для использования чата.</span>
                <button
                  type="button"
                  onClick={() => setShowAuthNotice(false)}
                  className="shrink-0 p-1 rounded hover:bg-amber-100 text-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-400"
                  aria-label="Закрыть"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="text-sm text-neutral-text/70 bg-neutral-light rounded-lg px-4 py-3">
                Войдите, чтобы задавать вопросы. Пока можно просмотреть предложенные темы ниже.
              </div>
            )}
          </div>
        ) : (
          <div className={floatingInput ? "flex-shrink-0 px-6 pb-5 pt-2" : `border-t border-neutral-border flex-shrink-0 bg-white ${contentPadding} pl-4 pr-4`}>
          <div className={floatingInput ? "bg-white rounded-2xl shadow-lg border border-neutral-border/40 px-4 py-3" : ""}>
            {recentQuestions.length > 0 && (
              <div className="mb-2">
                <button
                  type="button"
                  onClick={() => setRecentQuestionsExpanded((e) => !e)}
                  aria-expanded={recentQuestionsExpanded}
                  aria-label={recentQuestionsExpanded ? "Свернуть недавние вопросы" : "Развернуть недавние вопросы"}
                  className="flex items-center gap-1 text-xs text-neutral-text/60 hover:text-neutral-text/80 transition-colors"
                >
                  <span>Недавние</span>
                  {recentQuestionsExpanded ? (
                    <ChevronUp className="w-3.5 h-3.5 flex-shrink-0" aria-hidden />
                  ) : (
                    <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" aria-hidden />
                  )}
                </button>
                {recentQuestionsExpanded && (
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {recentQuestions.slice(0, 5).map((q) => (
                      <button
                        key={q}
                        type="button"
                        onClick={() => sendMessage(q)}
                        disabled={isLoading}
                        className="text-xs px-2.5 py-1 rounded-md bg-neutral-light border border-neutral-border text-neutral-text hover:bg-primary/10 hover:border-primary/30 hover:text-primary transition truncate max-w-[200px] disabled:opacity-50 disabled:cursor-not-allowed"
                        title={q}
                      >
                        {q.length > 35 ? q.slice(0, 34) + "…" : q}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="flex gap-2 min-w-0">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), handleSend())}
                placeholder={placeholder}
                className={`flex-1 px-3 py-2 text-sm rounded-lg resize-none placeholder:text-neutral-text/50 disabled:cursor-not-allowed focus:outline-none ${
                  floatingInput
                    ? "border-0 bg-transparent focus:ring-0"
                    : "border border-neutral-border bg-neutral-light focus:ring-2 focus:ring-primary/30 focus:border-primary/50"
                }`}
                rows={2}
              />
              {isLoading ? (
                <button
                  onClick={() => abortControllerRef.current?.abort()}
                  className="px-4 py-2 min-h-[44px] min-w-[44px] bg-neutral-border text-neutral-text rounded-lg hover:bg-neutral-border/80 transition flex items-center justify-center flex-shrink-0 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  title="Остановить генерацию"
                >
                  <Square size={18} />
                </button>
              ) : (
                <button
                  onClick={handleSend}
                  disabled={!input.trim()}
                  className="px-4 py-2 min-h-[44px] min-w-[44px] bg-primary text-white rounded-lg hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center flex-shrink-0 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  <Send size={18} />
                </button>
              )}
            </div>
            <p className="mt-1.5 text-xs text-neutral-text/50 text-center select-none">
             AI Clickhouse Expert может ошибаться. Проверяйте важные запросы в{" "}
              <a href="/sandbox" className="underline hover:text-primary transition-colors">
                Sandbox
              </a>{" "}
              и официальной{" "}
              <a
                href="https://clickhouse.com/docs"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-primary transition-colors"
              >
                документации ClickHouse
              </a>
              .
            </p>
            {CAN_DEBUG_PROMPTS && (
              <label className="mt-2 flex items-center gap-2 text-sm text-neutral-text/70 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={debugMode}
                  onChange={(e) => setDebugMode(e.target.checked)}
                  className="rounded border-neutral-border text-primary focus:ring-primary/30"
                />
                Режим отладки (промпты и RAG в ответе)
              </label>
            )}
          </div>
          </div>
        )}
      </div>
      </div>

      {feedbackDislikeMessageIndex !== null && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => {
            setFeedbackDislikeMessageIndex(null);
            setFeedbackDislikeReason("");
            setFeedbackDislikeComment("");
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Причина дизлайка"
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-sm w-full border border-neutral-border p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-sm font-semibold text-neutral-text mb-3">Что не так с ответом?</h3>
            <div className="space-y-2 mb-4">
              {FEEDBACK_DISLIKE_REASONS.map((r) => (
                <label key={r.value} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="feedback-reason"
                    value={r.value}
                    checked={feedbackDislikeReason === r.value}
                    onChange={() => setFeedbackDislikeReason(r.value)}
                    className="rounded-full border-neutral-border text-primary focus:ring-primary/30"
                  />
                  <span className="text-sm text-neutral-text">{r.label}</span>
                </label>
              ))}
            </div>
            <textarea
              placeholder="Комментарий (необязательно)"
              value={feedbackDislikeComment}
              onChange={(e) => setFeedbackDislikeComment(e.target.value)}
              className="w-full text-sm border border-neutral-border rounded-lg p-2 min-h-[60px] resize-none placeholder:text-neutral-text/50 focus:outline-none focus:ring-2 focus:ring-primary/30"
              rows={2}
            />
            <div className="flex gap-2 mt-4">
              <button
                type="button"
                onClick={() => {
                  setFeedbackDislikeMessageIndex(null);
                  setFeedbackDislikeReason("");
                  setFeedbackDislikeComment("");
                }}
                className="flex-1 py-2 text-sm font-medium rounded-lg border border-neutral-border bg-white text-neutral-text hover:bg-neutral-light transition"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={() => {
                  const idx = feedbackDislikeMessageIndex;
                  if (idx == null) return;
                  const reasonStr = feedbackDislikeReason || "other";
                  const feedbackReason =
                    reasonStr === "other" && feedbackDislikeComment.trim()
                      ? `other: ${feedbackDislikeComment.trim()}`
                      : reasonStr;
                  const comment = feedbackDislikeComment.trim() || undefined;
                  setFeedbackDislikeMessageIndex(null);
                  setFeedbackDislikeReason("");
                  setFeedbackDislikeComment("");
                  sendFeedback(idx, "bad", comment, feedbackReason);
                }}
                className="flex-1 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary-dark transition disabled:opacity-50"
              >
                Отправить
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {CAN_DEBUG_PROMPTS && debugModalMessage?.debug && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => setDebugModalMessage(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Отладка промптов"
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col border border-neutral-border"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-border flex-shrink-0">
              <h3 className="text-sm font-semibold text-neutral-text">Отладка: системный промпт, user prompt, RAG</h3>
              <button
                type="button"
                onClick={() => setDebugModalMessage(null)}
                className="p-1.5 rounded-lg hover:bg-neutral-light text-neutral-text/70 hover:text-neutral-text"
                aria-label="Закрыть"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <section>
                <h4 className="text-xs font-medium text-neutral-text/70 mb-1">Системный промпт</h4>
                <pre className="text-xs bg-neutral-light border border-neutral-border rounded-lg p-3 overflow-x-auto overflow-y-auto max-h-48 whitespace-pre-wrap break-words">
                  {debugModalMessage.debug.system_prompt || "(пусто)"}
                </pre>
              </section>
              <section>
                <h4 className="text-xs font-medium text-neutral-text/70 mb-1">User prompt (сообщение в LLM после нормализации)</h4>
                <pre className="text-xs bg-neutral-light border border-neutral-border rounded-lg p-3 overflow-x-auto overflow-y-auto max-h-48 whitespace-pre-wrap break-words">
                  {debugModalMessage.debug.user_prompt || "(пусто)"}
                </pre>
              </section>
              <section>
                <h4 className="text-xs font-medium text-neutral-text/70 mb-1">RAG-контекст</h4>
                <pre className="text-xs bg-neutral-light border border-neutral-border rounded-lg p-3 overflow-x-auto overflow-y-auto max-h-64 whitespace-pre-wrap break-words">
                  {debugModalMessage.debug.rag_context || "(пусто)"}
                </pre>
              </section>
            </div>
          </div>
        </div>,
        document.body
      )}

      {settingsOpen && user && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => setSettingsOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Настройки контекста для AI Tutor"
        >
          <div
            className="w-full max-w-md max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <UserContextSurvey
              userId={user.id}
              initialAnswers={
                typeof user.user_context === "string"
                  ? (() => {
                      try {
                        return JSON.parse(user.user_context) as UserContextAnswers;
                      } catch {
                        return null;
                      }
                    })()
                  : (user.user_context as UserContextAnswers | undefined) ?? null
              }
              onClose={() => setSettingsOpen(false)}
            />
          </div>
        </div>,
        document.body
      )}

      {showProfileContextInvite && user && createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50"
          onClick={() => {
            setUserContextModalDismissed();
            setShowProfileContextInvite(false);
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Анкета для персонализации AI Tutor"
        >
          <div
            className="w-full max-w-md max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <UserContextSurvey
              userId={user.id}
              asOnboarding
              title="Расскажите немного о себе"
              initialAnswers={
                typeof user.user_context === "string"
                  ? (() => {
                      try {
                        return JSON.parse(user.user_context) as UserContextAnswers;
                      } catch {
                        return null;
                      }
                    })()
                  : (user.user_context as UserContextAnswers | undefined) ?? null
              }
              onSave={() => {
                void refreshUser();
                setShowProfileContextInvite(false);
              }}
              onClose={() => setShowProfileContextInvite(false)}
            />
          </div>
        </div>,
        document.body
      )}

      {showSidebarInline && (
        <ChatSidebar
          chatList={chatList}
          currentChatId={currentChatId}
          selectedGroup={selectedGroup}
          activeGroup={activeGroup}
          onSelectGroup={setSelectedGroup}
          onSelectChat={handleSwitchChat}
          onDeleteChat={handleDeleteChat}
          onCreateChat={handleCreateChatInSidebar}
        />
      )}

      {showMobileSidebarButton && mobileSidebarOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex"
            role="dialog"
            aria-modal="true"
            aria-label="История чатов"
          >
            <div
              className="absolute inset-0 bg-black/50"
              aria-hidden="true"
              onClick={() => setMobileSidebarOpen(false)}
            />
            <div className="relative flex flex-col w-[280px] max-w-[85vw] bg-white border-r border-neutral-border shadow-xl overflow-hidden">
              <div className="flex items-center justify-between flex-shrink-0 p-2 border-b border-neutral-border">
                <span className="text-sm font-semibold text-neutral-text">История чатов</span>
                <button
                  type="button"
                  onClick={() => setMobileSidebarOpen(false)}
                  className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-neutral-light text-neutral-text/60 hover:text-neutral-text"
                  aria-label="Закрыть"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 min-h-0 overflow-auto">
                <ChatSidebar
                  drawerMode
                  chatList={chatList}
                  currentChatId={currentChatId}
                  selectedGroup={selectedGroup}
                  activeGroup={activeGroup}
                  onSelectGroup={setSelectedGroup}
                  onSelectChat={(id) => {
                    handleSwitchChat(id);
                    setMobileSidebarOpen(false);
                  }}
                  onDeleteChat={handleDeleteChat}
                  onCreateChat={() => {
                    handleCreateChatInSidebar();
                    setMobileSidebarOpen(false);
                  }}
                />
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};
